/**
 * PasswordStrengthBar — visual indicator for password complexity.
 * Shared between register and reset-password screens.
 */
import { useMemo } from 'react'
import { View, Text } from 'react-native'
import { useTranslation } from 'react-i18next'
import Animated from 'react-native-reanimated'
import { FONTS, RADIUS, SPACING } from '../../lib/brand'
import { useSemanticColors } from '../../lib/theme/tokens'
import { useEntrance } from '../../lib/hooks/use-entrance'

interface PasswordStrengthBarProps {
  password: string
  isDark: boolean
  testID?: string
}

export function PasswordStrengthBar({ password, isDark, testID }: PasswordStrengthBarProps) {
  const enter = useEntrance()
  const { t } = useTranslation()
  const colors = useSemanticColors(isDark)

  const strength = useMemo(() => {
    let s = 0
    if (password.length >= 8) s++
    if (/[a-z]/.test(password)) s++
    if (/[A-Z]/.test(password)) s++
    if (/\d/.test(password)) s++
    if (/[^A-Za-z0-9]/.test(password) || password.length >= 12) s++
    return s
  }, [password])

  const configs = [
    { color: colors.textError, label: t('register.strength_weak') },
    { color: colors.warning, label: t('register.strength_fair') },
    { color: colors.warning, label: t('register.strength_fair') },
    { color: colors.success, label: t('register.strength_good') },
    { color: colors.success, label: t('register.strength_strong') },
  ]

  // The meter used to unmount itself (`return null`) below 3 characters and
  // remount at the first strength tier, so the confirm-password field and the
  // CTA below it jumped by a whole row on the third keystroke. The row is now
  // always reserved at a fixed height and only its contents appear.
  const cfg = password.length >= 3 && strength > 0 ? configs[strength - 1] : undefined

  return (
    <Animated.View
      collapsable={false}
      entering={enter()}
      testID={testID}
      accessibilityElementsHidden={!cfg}
      importantForAccessibility={cfg ? 'auto' : 'no-hide-descendants'}
      style={{ marginTop: -10, marginBottom: 4, height: SPACING.lg }}
    >
      {cfg ? (
        <>
          <View
            style={{
              height: 3,
              backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : colors.border,
              borderRadius: RADIUS.xs,
              overflow: 'hidden',
            }}
          >
            <View
              style={{
                height: 3,
                width: `${strength * 20}%`,
                backgroundColor: cfg.color,
                borderRadius: RADIUS.xs,
              }}
            />
          </View>
          <Text
            numberOfLines={1}
            style={{
              fontFamily: FONTS.body,
              fontSize: 12,
              lineHeight: 16,
              color: cfg.color,
              marginTop: 4,
              textAlign: 'right',
            }}
          >
            {cfg.label}
          </Text>
        </>
      ) : null}
    </Animated.View>
  )
}
